import { inspect } from 'util';
import { RequestProps, KeysRequest, elmType, indexType } from './../index.types';
import { DATALAYER_CONSTANT } from '../../../constants';
import { DataLayerProps } from './index.types';
import { canUseDOM, getProcessEnvs, logger } from '@marriott/mi-headless-utils';

const { DATALAYER_ENDPOINT } = process.env;

const MOCK_SESSION_ID = '0D96A1BF-70D3-5443-BB54-D0DF6158EAA8';

export const getDataLayer = async (
  req: {
    cookies?: { sessionID: string; UserIdToken: string };
    headers: RequestProps;
    originalUrl: string;
  },
  resolvedUrl: string,
  locale: string
): Promise<DataLayerProps> => {
  // logger initialization
  const { log } = canUseDOM
    ? logger({ requestID: '', sessionID: '' })('Adobe DataLayer')
    : global.loggerInstance('Datalayer');
  const adobeDataLayer = [];
  const headersKey: Record<string, string | undefined> = {};
  const localeKeyValue = locale;
  const theaders = canUseDOM ? ({} as RequestProps) : req.headers;
  const cookies = canUseDOM ? document.cookie : req.headers.cookie;
  const pageUrl = resolvedUrl ? resolvedUrl.split('?')[0] : '';

  const ENV_PLATFORM = 'env_platform';

  const payLoad = {
    sessionToken: req?.cookies?.sessionID ?? MOCK_SESSION_ID,
    sourceURI: `/${locale}${pageUrl}`,
    context: {
      localeKey: localeKeyValue ?? 'en_US',
      pageURI: `/${locale}${pageUrl}`,
      absolutePageURL: `/${locale}${pageUrl}`,
    },
  };

  // creating headers for datalayer call
  DATALAYER_CONSTANT.headerKeys.forEach(name => {
    if (name && theaders[name as KeysRequest['keys']]) {
      headersKey[name] = theaders[name as KeysRequest['keys']];
    }
  });

  const config = {
    headers: {
      'Content-Type': 'application/json',
      'x-host': headersKey['x-host'] ?? '',
      requestID: req?.headers['x-request-id'],
      cookie: cookies ?? '',
    },
  };

  const endpoint = DATALAYER_ENDPOINT ?? '';

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      ...config.headers,
    },
    body: JSON.stringify(payLoad),
  })
    .then(async response => {
      const status = response.status;
      const data = await response.json();
      log.debug(`DataLayer Call Success: ${inspect(status)}`);
      log.debug(`Datalayer Payload: ${inspect(payLoad)}`);
      log.debug(`Datalayer Config: ${inspect(config)}`);
      log.debug(`Datalayer Endpoint: ${inspect(endpoint)}`);
      return data.component?.data;
    })
    .catch(err => {
      log.error(
        `[${inspect(endpoint)}] [DataLayer] API Failed: ${inspect(err?.response?.status)} [message]: ${inspect(
          err?.response?.data
        )}`
      );
      log.debug(`Datalayer Payload: ${inspect(payLoad)}`);
      log.debug(`Datalayer Config: ${inspect(config)}`);
      log.debug(`Datalayer Endpoint: ${inspect(endpoint)}`);
      return Promise.reject(err);
    });

  const dataLayerObj: indexType = {};
  const envProcessURL = canUseDOM ? getProcessEnvs()['PHOENIX_ENV_PLATFORM'] : process.env['PHOENIX_ENV_PLATFORM'];

  /*
  manipulating some of the response data
  */
  if (typeof response.dataProperties !== 'undefined') {
    response.dataProperties.forEach((elm: elmType) => {
      if (elm.value && elm.value !== 'undefined' && elm.value !== null) {
        dataLayerObj[elm.key] = elm.value;
      }
    });
  }

  // To set platform like "AEM-prod, AEM-stage, AEM-dev"
  if (envProcessURL) {
    dataLayerObj[ENV_PLATFORM] = envProcessURL;
  }

  // logging
  const isLogError = canUseDOM
    ? getProcessEnvs()['LOG_DATA_LAYER'] === 'true'
    : process.env['LOG_DATA_LAYER'] === 'true';

  if (isLogError) {
    log.error(
      `Datalayer endpoint: ${endpoint}, datalayer env. value ${
        canUseDOM ? getProcessEnvs()['DATALAYER_ENDPOINT'] : process.env['DATALAYER_ENDPOINT']
      }`
    );
    log.error(`Datalayer request headers: ${inspect(config.headers, { breakLength: Infinity })}`);
    log.error(`Datalayer fetch request headers: ${inspect(headersKey, { breakLength: Infinity })}`);
    log.error(`Datalayer fetch post data: ${inspect(payLoad, { breakLength: Infinity })}`);

    log.debug(`Datalayer response: ${inspect(response, { breakLength: Infinity })}`);
  }

  adobeDataLayer.push(dataLayerObj);

  return {
    data: adobeDataLayer,
  };
};
